import {
  Button,
  Col,
  DatePicker,
  Form,
  Modal,
  Row,
  Space,
  Typography,
  message,
} from 'antd';
import {
  checkDateIsAvailable,
  endTimeValidator,
  getFreezeDetailData,
  labelStyle,
  numberToChinese,
  removeDurationFromArray,
  startTimeValidator,
} from '../helper';
import moment, { type Moment } from 'moment';
import React, { useState } from 'react';

import { ExclamationCircleOutlined } from '@ant-design/icons';

import type {
  FreezeFormValue,
  MemberFreezeAddRequest,
  MemberFreezeDetailResponse,
  MemberFreezeEditRequest,
  MemberFreezeRemoveRequest,
  MemberFreezeResponse,
} from '../types';
import styles from './index.module.scss';
import sharedStyles from '../sharedStyles.module.scss';
import axios from 'axios';
import type { FormInstance } from 'antd/es/form/Form';
import {
  type ErrorResponse,
  asyncErrorHandler,
} from '@/utils/asyncErrorHandler';
import HistoryDurations from '../HistoryDurations';

interface EditOrFinishFreezeProps {
  record: MemberFreezeResponse;
  getTableData: () => void;
  isDeleteButton: boolean;
}

const { Item, useForm } = Form;
const { Text, Title } = Typography;
const { confirm } = Modal;

const EditOrFinishFreeze = ({
  record,
  getTableData,
  isDeleteButton,
}: EditOrFinishFreezeProps) => {
  const [editFreezeForm] = useForm();
  const [editDurationForm] = useForm();

  const [isOpenEditFreezeModal, setIsOpenEditFreezeModal] = useState(false);
  const [freezeDetail, setFreezeDetail] =
    useState<MemberFreezeDetailResponse>();
  const [isEditDuration, setIsEditDuration] = useState(false);
  const [historyIndex, setHistoryIndex] = useState<number | undefined>(
    undefined
  );
  const [selectedFreezeDuration, setSelectedFreezeDuration] =
    useState<FreezeFormValue>();

  const handleEditFreezeClick = async () => {
    const { _id, subscriptionPlanId } = record;
    try {
      const data = await getFreezeDetailData(_id, subscriptionPlanId);
      setFreezeDetail(data);
      setIsOpenEditFreezeModal(true);
    } catch (error) {
      const errObj: ErrorResponse = error.response.data;
      asyncErrorHandler(errObj);
    }
  };

  const handleDurationClick = (index: number) => {
    editDurationForm.setFieldsValue({
      freezeStartTime: moment(
        freezeDetail?.freezeHistory[index].freezeStartTime
      ),
      freezeEndTime: moment(freezeDetail?.freezeHistory[index].freezeEndTime),
    });
    setHistoryIndex(index);
    setIsEditDuration(true);
  };

  const handleFormValuesChange = (
    changedValues: {
      freezeStartTime: Moment;
      freezeEndTime: Moment;
    },
    form: FormInstance
  ) => {
    const freezeDuration = { ...selectedFreezeDuration, ...changedValues };
    form.setFields([
      {
        name: 'freezeStartTime',
        errors: [],
      },
      {
        name: 'freezeEndTime',
        errors: [],
      },
    ]);
    setSelectedFreezeDuration(freezeDuration);
  };

  const addDuration = async (data: MemberFreezeAddRequest) => {
    confirm({
      title: <Title level={4}>確定新增凍結?</Title>,
      content: '確定請按確認鍵',
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        try {
          const res = await axios({
            method: 'POST',
            url: '/ezcon/api/memberFreezeManagementAdd',
            data,
            headers: {
              authorization: localStorage.getItem('feathers-jwt'),
            },
          });
          editFreezeForm.resetFields();
          setSelectedFreezeDuration(undefined);
          setFreezeDetail(res.data);
          getTableData();
          message.success('新增凍結成功!');
        } catch (error) {
          const errObj: ErrorResponse = error.response.data;
          asyncErrorHandler(errObj);
        }
      },
    });
  };

  const addFreezeDuration = async (formValues: FreezeFormValue) => {
    const { _id, subscriptionPlanId } = record;
    const { freezeStartTime, freezeEndTime } = formValues;

    const data = {
      _id,
      subscriptionPlanId,
      freezeStartTime: moment(freezeStartTime).valueOf(),
      freezeEndTime: moment(freezeEndTime).valueOf(),
    };

    addDuration(data);
  };

  const editDuration = async (data: MemberFreezeEditRequest) => {
    confirm({
      title: <Title level={4}>確定修改凍結?</Title>,
      content: '確定請按確認鍵',
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        try {
          const res = await axios({
            method: 'PUT',
            url: '/ezcon/api/memberFreezeManagementEdit',
            data,
            headers: {
              authorization: localStorage.getItem('feathers-jwt'),
            },
          });
          setSelectedFreezeDuration(undefined);
          setFreezeDetail(res.data);
          getTableData();
          message.success('編輯凍結期間成功!');
        } catch (error) {
          const errObj: ErrorResponse = error.response.data;
          asyncErrorHandler(errObj);
        }
      },
    });
  };

  const handleEditDurationFormSubmit = async (formValues: FreezeFormValue) => {
    const { _id, subscriptionPlanId } = record;
    const { freezeStartTime, freezeEndTime } = formValues;

    const data = {
      _id,
      subscriptionPlanId,
      index: historyIndex as number,
      freezeStartTime: moment(freezeStartTime).valueOf(),
      freezeEndTime: moment(freezeEndTime).valueOf(),
    };

    editDuration(data);
  };

  const closeEditFreezeModal = () => {
    setSelectedFreezeDuration(undefined);
    setIsOpenEditFreezeModal(false);
  };

  const deleteFreezeDuration = (
    requestData: MemberFreezeRemoveRequest,
    functionType: 'delete' | 'finish'
  ) => {
    confirm({
      title: (
        <Title level={4}>
          {functionType === 'delete' ? '確定刪除凍結?' : '確定結束凍結?'}
        </Title>
      ),
      content: '確定請按確認鍵',
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        try {
          const res = await axios({
            method: 'DELETE',
            url: '/ezcon/api/memberFreezeManagementRemove',
            data: requestData,
            headers: {
              authorization: localStorage.getItem('feathers-jwt'),
            },
          });
          setFreezeDetail(res.data);
          getTableData();
          setIsEditDuration(false);
          message.success(
            functionType === 'delete' ? '刪除凍結成功!' : '結束凍結成功!'
          );
        } catch (error) {
          const errObj: ErrorResponse = error.response.data;
          asyncErrorHandler(
            errObj,
            functionType === 'delete' ? '刪除凍結失敗!' : '結束凍結失敗!'
          );
        }
      },
    });
  };

  const showDeleteConfirm = async (functionType: 'delete' | 'finish') => {
    const { _id, subscriptionPlanId } = record;

    const data: MemberFreezeRemoveRequest = {
      _id,
      subscriptionPlanId,
    };

    if (functionType === 'delete') {
      data.index = historyIndex;
    }

    deleteFreezeDuration(data, functionType);
  };

  return (
    <>
      <Button
        type={isDeleteButton === false ? 'default' : 'primary'}
        danger={isDeleteButton}
        onClick={
          isDeleteButton === false
            ? handleEditFreezeClick
            : () => showDeleteConfirm('finish')
        }
      >
        {isDeleteButton === false ? '編輯凍結' : '結束凍結'}
      </Button>
      <Modal
        open={isOpenEditFreezeModal}
        centered
        destroyOnClose
        maskClosable={false}
        title="編輯凍結"
        footer={null}
        onCancel={closeEditFreezeModal}
      >
        <Form
          form={editFreezeForm}
          layout="vertical"
          preserve={false}
          onFinish={addFreezeDuration}
          onValuesChange={(changedValues) =>
            handleFormValuesChange(changedValues, editFreezeForm)
          }
        >
          <Item
            label={<Text type="secondary">剩餘凍結次數</Text>}
            labelCol={labelStyle}
          >
            <span>
              {(freezeDetail?.freezeCountLeft ?? record.freezeCountLeft) + '次'}
            </span>
          </Item>
          <Item
            label={<Text type="secondary">凍結剩餘天數</Text>}
            labelCol={labelStyle}
          >
            <span>
              {moment(
                freezeDetail?.freezeDurationLeft ?? record.freezeDurationLeft
              ).diff(moment(0), 'day') + '天'}
            </span>
          </Item>
          {freezeDetail?.freezeHistory &&
          freezeDetail.freezeHistory.length > 0 ? (
            <HistoryDurations
              freezeDetail={freezeDetail}
              handleDurationClick={handleDurationClick}
              freezeDuration={selectedFreezeDuration}
              index={freezeDetail.freezeHistory.length + 1}
            />
          ) : (
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Item
                  label={<Text type="secondary">凍結開始日期</Text>}
                  name="freezeStartTime"
                  rules={[
                    {
                      required: true,
                      validator: (_rules, value) =>
                        startTimeValidator({
                          freezeDetail,
                          freezeStartTime: value,
                          freezeEndTime:
                            editDurationForm.getFieldValue('freezeEndTime'),
                        }),
                    },
                  ]}
                >
                  <DatePicker
                    placeholder="請選擇凍結開始日期"
                    className={sharedStyles.widthFull}
                    format="YYYY/MM/DD"
                    disabledDate={
                      freezeDetail &&
                      ((date) =>
                        checkDateIsAvailable({
                          date,
                          freezeHistory: freezeDetail.freezeHistory,
                          historyIndex,
                        }))
                    }
                  />
                </Item>
              </Col>
              <Col span={12}>
                <Item
                  label={<Text type="secondary">凍結結束日期</Text>}
                  name="freezeEndTime"
                  rules={[
                    {
                      required: true,
                      validator: (_rules, value) =>
                        endTimeValidator({
                          freezeStartTime:
                            selectedFreezeDuration?.freezeStartTime,
                          freezeEndTime: value,
                          freezeDetail,
                        }),
                    },
                  ]}
                >
                  <DatePicker
                    placeholder="請選擇凍結結束日期"
                    className={sharedStyles.widthFull}
                    format="YYYY/MM/DD"
                    disabledDate={
                      freezeDetail &&
                      ((date) =>
                        checkDateIsAvailable({
                          date,
                          freezeHistory: freezeDetail.freezeHistory,
                          historyIndex,
                        }))
                    }
                  />
                </Item>
              </Col>
            </Row>
          )}
        </Form>
        <Space className={styles.footerButtonContainer}>
          <Button
            onClick={
              freezeDetail?.freezeCountLeft === 0 ||
              freezeDetail?.freezeDurationLeft === 0
                ? closeEditFreezeModal
                : editFreezeForm.submit
            }
          >
            確認
          </Button>
        </Space>
      </Modal>
      <Modal
        open={isEditDuration}
        centered
        destroyOnClose
        maskClosable={false}
        title={
          historyIndex !== undefined
            ? `編輯第${numberToChinese(historyIndex)}次凍結`
            : '編輯凍結期間'
        }
        footer={null}
        onCancel={() => setIsEditDuration(false)}
      >
        <Form
          form={editDurationForm}
          layout="vertical"
          preserve={false}
          onFinish={handleEditDurationFormSubmit}
          onValuesChange={(changedValues) =>
            handleFormValuesChange(changedValues, editDurationForm)
          }
        >
          <Item
            label={<Text type="secondary">剩餘凍結次數</Text>}
            labelCol={labelStyle}
          >
            <span>
              {(freezeDetail?.freezeCountLeft ?? record.freezeCountLeft) + '次'}
            </span>
          </Item>
          <Item
            label={<Text type="secondary">凍結剩餘天數</Text>}
            labelCol={labelStyle}
          >
            <span>
              {moment(
                freezeDetail?.freezeDurationLeft ?? record.freezeDurationLeft
              ).diff(moment(0), 'day') + '天'}
            </span>
          </Item>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Item
                label={<Text type="secondary">凍結開始日期</Text>}
                name="freezeStartTime"
                rules={[
                  {
                    required: true,
                    validator: (_rules, value) => {
                      const newHistoryDurations = removeDurationFromArray(
                        freezeDetail?.freezeHistory,
                        historyIndex as number
                      );
                      if (!newHistoryDurations) {
                        return;
                      }
                      const newFreezeDetail = {
                        freezeHistory: newHistoryDurations,
                        freezeDurationLeft:
                          freezeDetail?.freezeDurationLeft ??
                          record.freezeDurationLeft,
                        freezeCountLeft:
                          freezeDetail?.freezeCountLeft ??
                          record.freezeCountLeft,
                      };
                      return startTimeValidator({
                        freezeDetail: newFreezeDetail,
                        freezeStartTime: value,
                        freezeEndTime: selectedFreezeDuration?.freezeEndTime,
                      });
                    },
                  },
                ]}
              >
                <DatePicker
                  placeholder="請選擇凍結開始日期"
                  className={sharedStyles.widthFull}
                  format="YYYY/MM/DD"
                  disabled={
                    editDurationForm.getFieldValue('freezeStartTime') &&
                    editDurationForm
                      .getFieldValue('freezeStartTime')
                      .isBefore(moment(), 'day')
                  }
                  disabledDate={
                    freezeDetail &&
                    ((date) =>
                      checkDateIsAvailable({
                        date,
                        freezeHistory: freezeDetail.freezeHistory,
                        historyIndex,
                      }))
                  }
                />
              </Item>
            </Col>
            <Col span={12}>
              <Item
                label={<Text type="secondary">凍結結束日期</Text>}
                name="freezeEndTime"
                rules={[
                  {
                    required: true,
                    validator: (_rules, value) => {
                      const newHistoryDurations = removeDurationFromArray(
                        freezeDetail?.freezeHistory,
                        historyIndex as number
                      );
                      if (!newHistoryDurations) {
                        return;
                      }
                      const newFreezeDetail = {
                        freezeHistory: newHistoryDurations,
                        freezeDurationLeft:
                          freezeDetail?.freezeDurationLeft ??
                          record.freezeDurationLeft,
                        freezeCountLeft:
                          freezeDetail?.freezeCountLeft ??
                          record.freezeCountLeft,
                      };
                      return endTimeValidator({
                        freezeDetail: newFreezeDetail,
                        freezeStartTime:
                          selectedFreezeDuration?.freezeStartTime,
                        freezeEndTime: value,
                      });
                    },
                  },
                ]}
              >
                <DatePicker
                  placeholder="請選擇凍結結束日期"
                  className={sharedStyles.widthFull}
                  format="YYYY/MM/DD"
                  disabledDate={
                    freezeDetail &&
                    ((date) =>
                      checkDateIsAvailable({
                        date,
                        freezeHistory: freezeDetail.freezeHistory,
                        historyIndex,
                      }))
                  }
                />
              </Item>
            </Col>
          </Row>
          <Item>
            <Space className={styles.footerButtonContainer}>
              <Button
                type="primary"
                onClick={() => showDeleteConfirm('delete')}
              >
                刪除凍結
              </Button>
              <Button htmlType="submit">儲存修改</Button>
            </Space>
          </Item>
        </Form>
      </Modal>
    </>
  );
};

export default EditOrFinishFreeze;
